import moment from 'moment-timezone';
import { isMoment } from 'moment/moment';
import { translate } from 'APP_ROOT/i18next';

// Default format for all benchmark dates.
export const BENCHMARK_GENERIC_ISO_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
export const BENCHMARK_DATE_TIMEZ_FORMAT = 'MM/DD/YYYY, HH:mm:ss z';
export const BENCHMARK_DATE_TIMEZ_FORMAT_WITH_PREPOSITION = translate('containers.reports.contributorLastSavedDateFormat');
export const BENCHMARK_DATE_TIMEZ_FORMAT_ISO = 'YYYY-MM-DDTHH:mm:ss.000Z';
export const BENCHMARK_DATE_TIMEZ_FORMAT_HUMAN = 'MMMM D, YYYY h:mm z';
export const BENCHMARK_DATE_TIME_FORMAT = 'MM/DD/YYYY, HH:mm:ss';
export const BENCHMARK_DATE_TIME_FORMAT_WO_COMMA = 'MM/DD/YYYY HH:mm:ss';
export const BENCHMARK_DATE_FORMAT = 'MM/DD/YYYY';
export const BENCHMARK_DATE_FORMAT_HYPHEN = 'YYYY-MM-DD';
export const BENCHMARK_TIME_FORMAT = 'HH:mm';
export const BENCHMARK_TIMEZ_FORMAT = 'HH:mm z';
export const BENCHMARK_TIMEA_FORMAT = 'hh:mm a';
export const BENCHMARK_BIRTHDAY_FORMAT = 'MM/DD/YY';
export const BENCHMARK_COMPLETE_BIRTHDAY_FORMAT = 'MM/DD/YYYY';
export const BENCHMARK_DATE_FORMAT_HUMAN = 'MMMM D, YYYY';
export const BENCHMARK_DATE_TIMEZ_FORMAT_ISO_WITH_MILLISECONDS =
  'YYYY-MM-DDTHH:mm:ss.SSS[Z]';
/**
 * @param date
 * @param {string} timezone
 * @param {string} format
 * @param {boolean} useTimezone
 * @return {object} A moment instance with the applied timezone.
 */
export function momentWithTZ(date, timezone, format, useTimezone = true) {
  const isBirthdayFormat = typeof date === 'string' && date.length === 8;
  const dateStringFormat = format
    ? format
    : isBirthdayFormat
    ? BENCHMARK_DATE_FORMAT
    : undefined;

  if (timezone && useTimezone) {
    return moment(date, dateStringFormat).tz(timezone);
  } else if (!useTimezone) {
    return moment(date, dateStringFormat);
  }

  throw new Error('the timezone argument is required.');
}

/**
 * Applies the timezone and format to the date and returns a formatted string.
 * @param {Date|string} date
 * @param {string} timezone
 * @param {string} format
 * @param {boolean} useTimezone
 * @return {object} A moment instance with an applied timezone and format.
 */
export default function parseDate(
  date,
  timezone = '',
  format = BENCHMARK_DATE_TIMEZ_FORMAT,
  useTimezone = true
) {
  if (!timezone) {
    timezone = 'Etc/UTC';
  }
  return momentWithTZ(date, timezone, '', useTimezone).format(format);
}

/**
 * @param {Date|string} date
 * @param {string} format
 *
 * @return {object} A moment instance with an applied timezone and format.
 */
export function parseDateNoTimeZone(
  date,
  format = BENCHMARK_DATE_TIMEZ_FORMAT
) {
  return moment.utc(date).format(format);
}

/**
 * @param {Date|string} date
 * @param {string} timezone
 * @param {dateTimezone} dateTimezone Optional.
 *
 * @return {object} A moment instance with an applied timezone.
 */
export const parseDateNoFormat = (date, timezone = null) =>
  momentWithTZ(date, timezone, '', true);

/**
 * @param {Date|string} date
 * @param {string} timezone
 * @param {string} format
 *
 * @return {object} A moment instance with an applied timezone.
 */
export const parseDateFromFormat = (date, timezone = null, format) =>
  momentWithTZ(date, timezone, format);

/**
 * @param {Date|string} date
 *
 * @return {object} A moment instance as utc.
 */
export const formatDate = date => {
  return moment(date).utc();
};

/**
 * @param {Date|string} date
 * @param {string} format
 * @return {object} A moment instance of utc string.
 */
export const formatDateString = (date, format) => {
  return moment(date)
    .utc()
    .format(format);
};

export const formatDateStringOrTimestamp = (datetime, format) => {
  if (isMoment(datetime)) {
    return datetime.format(format);
  }

  const dateTimeMoment = moment(datetime, format, true);

  if (dateTimeMoment.isValid()) {
    return dateTimeMoment.format(format);
  } else {
    const parsedIsoTimestamp = moment(datetime, moment.ISO_8601, true);

    if (parsedIsoTimestamp.isValid()) {
      return parsedIsoTimestamp.format(format);
    } else {
      return datetime;
    }
  }
};

/**
 * @param {Date|string} date
 * @param {string} format
 *
 * @return {object} A moment instance of utc with only dates.
 */
export const readOnlyFormatDateNoTimeZone = (date, format) => {
  const dateNoTimeFormat = date?.split('T')[0].replace(/-/g, '') || '';
  return moment(dateNoTimeFormat)
    .utc()
    .format(format);
};

/**
 * Sets the date time string to the optional timezone and returns it
 * in ISO 1801 format
 * @param {String} date moment compatible date
 * @param {String} timezone moment compatible timezone
 * @returns {String}
 */
export function toISOStringStartOfDay(date, timezone = 'Etc/UTC') {
  return moment(date)
    .tz(timezone)
    .startOf('Day')
    .toISOString();
}
/**
 * Applies the offset (subtract) to transform the UTC to the correct timezone.
 * @param {String} date  e.g. 03/22/2025
 * @param {String} timezone moment valid timezone
 * @returns {String} e.g. 2025-03-23T01:00:00.000Z
 */
export function dateToISODateApplyingOffset(date, timezone = 'Etc/UTC') {
  return moment
    .tz(date, 'UTC')
    .subtract(moment.tz(date, timezone).utcOffset(), 'minutes')
    .toISOString();
}

/**
 * Transforms a string with valid ISO date, to a moment containing timezone
 * When a non valid ISO date is provided it returns a simple cast if needed
 * @param {String} date ISO valid string e.g 2025-03-23T01:00:00.000Z
 * @param {String} timezone moment valid string
 * @returns {Object} moment instantiated date
 */
export function dateStringAsISOToMomentApplyingOffset(
  date = '',
  timezone = 'Etc/UTC'
) {
  if (typeof date === 'object') return date;
  if (date.includes('Z')) {
    return moment
      .tz(date, 'UTC')
      .add(moment.tz(date, timezone).utcOffset(), 'minutes');
  }
  return moment(date);
}

/**
 * Converts any UTC/ISO string or Date to a generic ISO string (YYYY-MM-DDTHH:mm:ssZ) in the target timezone.
 * Use this for internal date handling, storage, comparison, and backend communication.
 * This is the preferred method for general date handling as it provides clean ISO format without milliseconds
 * and proper timezone conversion.
 *
 * @param {string|Date} date - The input date (ISO string, UTC string, or Date object).
 * @param {string} timezone - The IANA timezone to apply (e.g. 'America/Puerto_Rico').
 * @returns {string} ISO string in the target timezone (e.g. '2025-09-01T22:04:53-04:00').
 *
 * Example:
 *   toGenericISODateString('2025-09-02T02:04:53.902Z', 'America/Puerto_Rico') // '2025-09-01T22:04:53-04:00'
 */
export function toGenericISODateString(date, timezone = 'Etc/UTC') {
  if (!date) return '';
  // Always parse as moment in UTC, then convert to target timezone
  const m = moment.utc(date).tz(timezone);
  return m.format(BENCHMARK_GENERIC_ISO_FORMAT);
}

/**
 * Converts a date to UTC ISO string with milliseconds for report snapshots.
 * Always outputs UTC format (moment.utc() handles conversion automatically).
 * Use this for compatibility with backend processes that expect 'YYYY-MM-DDTHH:mm:ss.SSS[Z]' format.
 * Created to be used by report generation and components that create/update dates for report snapshot data.
 *
 * @param {string|Date|Moment} date - Input date (ISO string, Date object, or Moment instance)
 * @returns {string} UTC ISO with milliseconds (e.g. '2025-08-06T17:49:54.598Z')
 */
export function toBenchmarkISOinMillisecondsDateString(date) {
  if (!date) return '';
  return moment
    .utc(date)
    .format(BENCHMARK_DATE_TIMEZ_FORMAT_ISO_WITH_MILLISECONDS);
}

/**
 * Formats an ISO date string to the desired format in the given timezone.
 * Use this instead of localizeDate when you want robust, always-correct timezone conversion from ISO.
 *
 * @param {string|Date} isoDate - The ISO date string or Date object (should be UTC/ISO).
 * @param {string} timezone - The IANA timezone to display in (e.g. 'America/Puerto_Rico').
 * @param {string} format - The output format (e.g. BENCHMARK_DATE_TIME_FORMAT).
 * @returns {string} The formatted date string in the target timezone.
 *
 * Example:
 *   formatISOToLocal('2025-09-02T02:04:53.902Z', 'America/Puerto_Rico', 'MM/DD/YYYY, HH:mm:ss')
 *   // '09/01/2025, 22:04:53'
 */
export function formatISOToLocal(
  isoDate,
  timezone = 'Etc/UTC',
  format = BENCHMARK_DATE_TIMEZ_FORMAT
) {
  if (!isoDate) return '';
  return moment
    .utc(isoDate)
    .tz(timezone)
    .format(format);
}
